<template>
  <!-- https://vcalendar.io/examples/datepickers.html#date-range -->
  <div class="flex flex-col sm:flex-row justify-start items-center">
    <DatePicker
      :model-value="value.start"
      :mode="mode"
      :is24hr="true"
      :masks="masks"
      :popover="{ visibility: 'click' }"
      v-bind="$attrs"
      @update:modelValue="($event: Date) => setDate('start', $event)"
    >
      <template #default="{ inputValue, togglePopover, inputEvents }">
        <div class="relative flex-grow">
          <Input
            :label="label"
            :aria-label="label"
            :placeholder="placeholderFrom ?? $t('from')"
            :model-value="inputValue"
            :error="errors?.start"
            size="13"
            class="my-0 text-gray-900"
            :force-label-to-top="true"
            :variant="variant"
            :disabled="disabled"
            :input-events="inputEvents"
            @click.stop="() => {
              if(!disabled) {
                togglePopover();
              }
            }"
          >
            <template #icon>
              <CalendarIcon />
            </template>
          </Input>
        </div>
      </template>
    </DatePicker>

    <span
      class="h-px w-2.5 mx-3.5 bg-element-neutral"
      :class="variant !== 'narrow' ? 'mt-7.5' : ''"
    />

    <DatePicker
      v-bind="$attrs"
      ref="endPicker"
      :model-value="value.end"
      :mode="mode"
      :is24hr="true"
      :masks="masks"
      :popover="{ visibility: 'click' }"
      @update:modelValue="($event: Date) => setDate('end', $event)"
    >
      <template #default="{ inputValue, togglePopover, inputEvents }">
        <div class="relative flex-grow" :class="variant !== 'narrow' ? 'mt-7.5' : ''">
          <Input
            :aria-label="label"
            :placeholder="placeholderTo ?? $t('to')"
            :model-value="inputValue"
            :error="errors?.end"
            size="13"
            class="my-0 text-gray-900"
            :variant="variant"
            :force-label-to-top="true"
            :disabled="disabled"
            :input-events="inputEvents"
            @click.stop="() => {
              if(!disabled) {
                togglePopover();
              }
            }"
          >
            <template #icon>
              <CalendarIcon />
            </template>
          </Input>
        </div>
      </template>
    </DatePicker>
    <span v-if="typeof errors === 'string'" class="error-msg">{{ errors }}</span>
  </div>
</template>

<script lang="ts" setup>
import { useVModel } from '@vueuse/core';
import { DatePicker } from 'v-calendar';
import Input from '@components/utils/inputs/Input.vue';
import CalendarIcon from '@images/calendar.svg?component';
import { onMounted, onUnmounted, ref} from 'vue';
import type { Variant } from './Field.vue';

interface Props {
  modelValue: {
    start: Date | null;
    end: Date | null;
  };
  masks?: {
    input: string;
  };
  label?: string;
  variant?: Variant;
  placeholderFrom?: string;
  placeholderTo?: string;
  disabled?: boolean;
  errors?: string[];
  mode?: 'date' | 'datetime';
}

const endPicker = ref();

const emit = defineEmits(['update:modelValue']);
const props = withDefaults(defineProps<Props>(), {
  masks: () => ({
    input: 'DD.MM.YYYY',
    inputTime24hr: ['HH:mm:ss', 'HH:mm'],
    inputDateTime: ['DD.MM.YYYY HH:mm:ss', 'DD.MM.YYYY HH:mm', 'DD.MM.YYYY'],
    inputDateTime24hr: ['DD.MM.YYYY HH:mm:ss', 'DD.MM.YYYY HH:mm', 'DD.MM.YYYY'],
  }),
  modelValue: () => ({
    start: null,
    end: null,
  }),
  mode: 'date',
});

const value = useVModel(props, 'modelValue', emit, { passive: true });

const setDate = (key: string, date: Date) => {
  if (key === 'start' && date) {
    endPicker.value?.showPopover();
  }

  value.value = {
    ...value.value,
    [key]: date,
  };
};

// Apologies for the hack below, v-calendar is sh*t
const stopPopoverHide = (e) => {
  e.stopImmediatePropagation();
};

onMounted(() => {
  document.addEventListener('hide-popover', stopPopoverHide, {
    capture: true,
  });
})

onUnmounted(() => {
  document.removeEventListener('hide-popover', stopPopoverHide);
})
</script>

<style lang="scss" scoped>
:deep(.icon) {
  @apply h-3/4;

  svg {
    @apply h-full;

    path[fill] {
      @apply fill-element-neutral;
      stroke: none;
    }
    path[stroke]  {
      @apply stroke-element-neutral;
      fill: none;
    }
  }
}

:deep(.vc-popover-content-wrapper) {
  z-index: 100;
}

.error-msg {
  @apply text-red-120;
  font-size: px(10);
  line-height: 1.4em;
}
</style>
